import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Coins, LogOut, Trophy, Check, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { usePlayer } from './PlayerAuth';
import { playerApi } from '../../lib/api';

export function CharacterArt({ c, size = 64 }) {
  return (
    <svg viewBox="-30 -30 60 60" width={size} height={size} style={{ filter: `drop-shadow(0 0 10px ${c.primary})` }}>
      <path d="M0,-22 L17,18 L0,9 L-17,18 Z" fill="none" stroke={c.primary} strokeWidth="3.5" strokeLinejoin="round" />
      <path d="M0,-8 L7,10 L0,6 L-7,10 Z" fill={c.secondary} />
      <circle cx="0" cy="22" r="3" fill={c.trail} opacity=".8" />
    </svg>
  );
}

function CharCard({ c, user, onChange }) {
  const [busy, setBusy] = useState(false);
  const owned = user.owned_characters.includes(c.id);
  const active = user.selected_character === c.id;
  const act = async () => {
    setBusy(true);
    try {
      if (owned) { await playerApi.select(c.id); onChange({ ...user, selected_character: c.id }); toast.success(`${c.name} selezionato`); }
      else { onChange(await playerApi.buy(c.id)); toast.success(`${c.name} sbloccato!`); }
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <div data-testid={`char-${c.id}`} className={`rounded-2xl border p-4 flex flex-col items-center gap-2 transition-colors ${active ? 'border-cyan-400 bg-cyan-500/10' : 'border-white/10 bg-white/[.02] hover:border-violet-400/40'}`}>
      <span className="t-label">{c.tier}</span>
      <CharacterArt c={c} />
      <div className="text-sm font-bold text-slate-100">{c.name}</div>
      <button data-testid={`char-action-${c.id}`} onClick={act} disabled={busy || active || (!owned && user.coins < c.price)}
        className={`w-full flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-[11px] font-bold transition-colors disabled:opacity-80 ${active ? 'text-cyan-200' : owned ? 'border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10' : 'bg-amber-500/20 border border-amber-400/40 text-amber-200 hover:bg-amber-500/30'}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : active ? <><Check className="h-3.5 w-3.5" />IN USO</> : owned ? 'USA' : <>{user.coins < c.price ? <Lock className="h-3 w-3" /> : <Coins className="h-3 w-3" />}{c.price}</>}
      </button>
    </div>
  );
}

export default function Profile() {
  const { user, checked, chars, setUser, logout } = usePlayer();
  if (!checked) return null;
  if (!user) return <Navigate to="/" replace />;
  const bests = Object.entries(user.best_scores || {}).sort((a, b) => b[1].score - a[1].score);
  return (
    <div data-testid="profile-view" className="titan-root min-h-[100dvh]">
      <header className="sticky top-0 z-30 pt-safe border-b border-violet-500/15 bg-titan-bg/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto h-14 px-4 sm:px-8 flex items-center gap-3">
          <Link to="/" data-testid="profile-back" className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-300 hover:bg-white/5"><ArrowLeft className="h-5 w-5" /></Link>
          <span className="text-sm font-bold text-slate-100">Profilo giocatore</span>
          <button data-testid="logout-btn" onClick={logout} className="ml-auto flex items-center gap-1.5 text-xs text-slate-400 hover:text-rose-300"><LogOut className="h-4 w-4" />Esci</button>
        </div>
      </header>
      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-8 py-8 space-y-8">
        <section className="glass glass-edge rounded-3xl p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-6 rise">
          {user.picture ? <img src={user.picture} alt="" className="h-20 w-20 rounded-2xl" /> : <span className="h-20 w-20 rounded-2xl bg-gradient-to-br from-violet-600 to-cyan-600 flex items-center justify-center text-3xl font-extrabold">{user.name[0]}</span>}
          <div className="flex-1 min-w-0">
            <h1 data-testid="profile-name" className="text-3xl sm:text-4xl font-extrabold text-white truncate">{user.name}</h1>
            <div className="text-xs text-slate-400 mt-1 truncate">{user.email} · {(user.providers || []).join(' + ')}</div>
          </div>
          <div className="flex gap-3">
            <div className="rounded-2xl border border-amber-400/30 bg-amber-500/10 px-5 py-3 text-center"><div className="t-label">Monete</div><div data-testid="profile-coins-total" className="text-2xl font-extrabold text-amber-300 flex items-center gap-1.5 justify-center"><Coins className="h-5 w-5" />{user.coins}</div></div>
            <div className="rounded-2xl border border-white/10 px-5 py-3 text-center"><div className="t-label">Partite</div><div className="text-2xl font-extrabold text-cyan-300">{user.games_played || 0}</div></div>
          </div>
        </section>
        <section>
          <div className="flex items-end justify-between mb-4"><h2 className="text-lg font-bold text-slate-100">Negozio personaggi</h2><span className="t-label">{user.owned_characters.length}/{chars.length} sbloccati</span></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{chars.map((c) => <CharCard key={c.id} c={c} user={user} onChange={setUser} />)}</div>
          <p className="text-[11px] text-slate-500 t-chat mt-3">Il personaggio in uso diventa il protagonista nei giochi compatibili. Guadagni monete raccogliendole in partita e in base al punteggio.</p>
        </section>
        <section>
          <h2 className="text-lg font-bold text-slate-100 mb-4 flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-300" />I miei record</h2>
          {bests.length === 0 ? <div className="glass rounded-2xl p-6 text-sm text-slate-400 t-chat">Gioca un titolo dello Store per salvare il tuo primo record.</div> : (
            <div data-testid="profile-records" className="glass rounded-2xl divide-y divide-white/5">
              {bests.map(([gid, b]) => (
                <Link key={gid} to={`/gioco/${gid}`} className="flex items-center justify-between px-5 py-3 hover:bg-white/[.03]">
                  <span className="text-sm text-slate-200">{b.title}</span><span className="text-sm font-bold text-cyan-300 tabular-nums">{b.score.toLocaleString('it-IT')}</span>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
